import { JERUSALEM } from "../ports/clock.js";

/**
 * What a caller may pass as an instant.
 *
 * The API returns ISO strings and Postgres `date` columns come back as
 * 'YYYY-MM-DD', so accepting them directly is the honest signature — the
 * alternative is `new Date(...)` at three hundred call sites, and every one of
 * those is a chance to forget the timezone.
 */
export type DateInput = Date | string | number;

function toDate(value: DateInput): Date {
  return value instanceof Date ? value : new Date(value);
}

/**
 * Dates always render in Asia/Jerusalem regardless of where the server runs,
 * because "the 15th" in this domain means the 15th in Israel.
 */
export function formatDate(date: DateInput, timeZone: string = JERUSALEM): string {
  return new Intl.DateTimeFormat("he-IL", {
    timeZone,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(toDate(date));
}

export function formatDateTime(date: DateInput, timeZone: string = JERUSALEM): string {
  return new Intl.DateTimeFormat("he-IL", {
    timeZone,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(toDate(date));
}

/** Long form for document headers: "15 במרץ 2026". */
export function formatDateLong(date: DateInput, timeZone: string = JERUSALEM): string {
  return new Intl.DateTimeFormat("he-IL", {
    timeZone,
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(toDate(date));
}

/**
 * Whole days between two instants, evaluated on calendar dates in the business
 * timezone. Escalation rungs and aging buckets are counted in days, so a
 * millisecond-based division would misreport across a DST boundary.
 */
export function daysBetween(from: Date, to: Date, timeZone: string = JERUSALEM): number {
  const startOfDay = (d: Date) => {
    const iso = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
    return Date.parse(`${iso}T00:00:00Z`);
  };
  return Math.round((startOfDay(to) - startOfDay(from)) / 86_400_000);
}

/** "00:42:13" — the running-timer readout. */
export function formatDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
}

/** Decimal hours for profitability arithmetic: 90 minutes reads as 1.5. */
export function minutesToHours(minutes: number): number {
  return Math.round((minutes / 60) * 100) / 100;
}
